import Quill from 'quill';
import 'quill/dist/quill.snow.css';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Font = Quill.import('attributors/class/font') as any;
Font.whitelist = ['bodoni-moda', 'eb-garamond', 'courier-prime'];
Quill.register(Font, true);

const GAME_COLORS = [
  '#ece3cf', // text
  '#c8a24c', // gold
  '#9a8f7a', // text-dim
  '#7a1620', // crimson
  '#6f9e54', // success
  '#0a0a0e', // bg
  '#14121a', // surface
];

const TOOLBAR = [
  [{ header: [1, 2, 3, false] }],
  [{ font: ['bodoni-moda', 'eb-garamond', 'courier-prime'] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ color: GAME_COLORS }, { background: GAME_COLORS }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ indent: '-1' }, { indent: '+1' }],
  ['link', 'blockquote'],
  [{ align: [] }],
  ['clean'],
];

const FORMATS = [
  'header', 'font', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'list', 'indent', 'link', 'blockquote', 'align',
];

const ALLOWED_FONTS = new Set(['bodoni-moda', 'eb-garamond', 'courier-prime']);
const ALLOWED_COLORS = new Set(GAME_COLORS.map((c) => c.toLowerCase()));
const ALLOWED_ATTRS = new Set(FORMATS);

function sanitizePaste(_node: Node, delta: { ops?: { attributes?: Record<string, unknown> }[] }) {
  if (!delta.ops) return delta;
  for (const op of delta.ops) {
    if (!op.attributes) continue;
    const clean: Record<string, unknown> = {};
    for (const key of Object.keys(op.attributes)) {
      if (!ALLOWED_ATTRS.has(key)) continue;
      const val = op.attributes[key];
      if ((key === 'color' || key === 'background') && typeof val === 'string') {
        if (ALLOWED_COLORS.has(val.toLowerCase())) clean[key] = val;
        continue;
      }
      if (key === 'font' && typeof val === 'string') {
        if (ALLOWED_FONTS.has(val)) clean[key] = val;
        continue;
      }
      clean[key] = val;
    }
    op.attributes = Object.keys(clean).length > 0 ? clean : undefined;
  }
  return delta;
}

export interface CmsEditor {
  getHtml(): string;
  setHtml(html: string): void;
  /** True once the admin has actually typed/formatted something. */
  isDirty(): boolean;
}

export function mountCmsEditor(container: HTMLElement, opts?: { value?: string; placeholder?: string }): CmsEditor {
  const quill = new Quill(container, {
    theme: 'snow',
    modules: {
      toolbar: TOOLBAR,
      clipboard: { matchers: [[Node.ELEMENT_NODE, sanitizePaste]] },
    },
    formats: FORMATS,
    placeholder: opts?.placeholder ?? 'Write content…',
  });

  // ── Content-preservation contract ──────────────────────────────────────
  // Quill re-serialises whatever it loads: <b>→<strong>, <i>→<em>,
  // <div>→<p>, &quot;→", &middot;→·, and it drops literal newlines/tabs
  // between tags plus anything outside FORMATS. Existing legal/news content
  // contains all of those, so round-tripping it through Quill would silently
  // rewrite live legal text.
  //
  // So we keep the string exactly as it arrived and hand it back untouched
  // unless the admin actually edited. That makes byte-identity independent
  // of Quill's internal normalisation.
  let pristine = opts?.value ?? '';
  let dirty = false;

  if (opts?.value) {
    quill.root.innerHTML = opts.value;
    // Flush the load mutation as 'silent' so Quill's own normalisation can
    // never be mistaken for a user edit by the listener registered below.
    quill.update('silent');
  }

  quill.on('text-change', (_delta, _old, source) => {
    if (source === 'user') dirty = true;
  });

  return {
    getHtml: () => {
      if (!dirty) return pristine;
      return quill.root.innerHTML === '<p><br></p>' ? '' : quill.root.innerHTML;
    },
    setHtml: (html: string) => {
      pristine = html;
      dirty = false;
      quill.root.innerHTML = html;
      quill.update('silent');
    },
    isDirty: () => dirty,
  };
}
